import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
  fetchWeeklyMeetingConfig,
  updateWeeklyMeetingConfig,
} from '../../../controllers/meetingsController';
import { getCurrencyDisplayLabel, normalizeCurrencyInput } from '../../../utils/currencyDisplay';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const FALLBACK_MEMBER_INCOME = '603.003.102';
const FALLBACK_GUEST_INCOME = '603.003.103';

const logoIncomeHelper = (defaultCode, overridden) =>
  overridden
    ? `Özel değer kayıtlı. Sistem varsayılanına dönmek için ${defaultCode} yazıp kaydedin.`
    : `Sistem varsayılanı (MealLogoDebt appsettings): ${defaultCode}. Override için değiştirin.`;

const WeeklyMeetingConfigDialog = ({ open, onClose, onUpdated }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setLoading(true);
    fetchWeeklyMeetingConfig()
      .then((data) => setConfig(data))
      .catch((e) => {
        setConfig({
          defaultMealPrice: '',
          currency: 'TRY',
          memberMealLogoIncomeArpCode: FALLBACK_MEMBER_INCOME,
          guestMealLogoIncomeArpCode: FALLBACK_GUEST_INCOME,
          defaultMemberMealLogoIncomeArpCode: FALLBACK_MEMBER_INCOME,
          defaultGuestMealLogoIncomeArpCode: FALLBACK_GUEST_INCOME,
        });
        setError(e?.message || 'Ayarlar yüklenemedi');
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    if (!config?.defaultMealPrice || Number(config.defaultMealPrice) <= 0) {
      setError('Geçerli bir birim fiyat giriniz');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateWeeklyMeetingConfig({
        defaultMealPrice: Number(config.defaultMealPrice),
        currency: config.currency || 'TRY',
        memberMealLogoIncomeArpCode: config.memberMealLogoIncomeArpCode?.trim() || '',
        guestMealLogoIncomeArpCode: config.guestMealLogoIncomeArpCode?.trim() || '',
      });
      toast.success('Haftalık toplantı ayarları güncellendi');
      if (onUpdated) onUpdated();
      onClose();
    } catch (e) {
      setError(e?.message || 'Güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const memberDefault = config?.defaultMemberMealLogoIncomeArpCode || FALLBACK_MEMBER_INCOME;
  const guestDefault = config?.defaultGuestMealLogoIncomeArpCode || FALLBACK_GUEST_INCOME;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Haftalık Toplantı Ayarları
        <IconButton onClick={onClose} size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" sx={{ fontSize: '13px' }}>
            Toplu borçlandırma ve otomatik katılım borçları birim fiyata göre hesaplanır. Logo borç fişinde borç üye
            carisine yazılır; aşağıdaki hesaplar fişin karşı gelir satırıdır (cari kart değildir).
          </Alert>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                label="Birim Fiyat"
                type="number"
                inputProps={{ step: '0.01', min: 0.01 }}
                value={config?.defaultMealPrice ?? ''}
                onChange={(e) => setConfig((prev) => ({ ...prev, defaultMealPrice: e.target.value }))}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Para Birimi"
                value={getCurrencyDisplayLabel(config?.currency || 'TRY')}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  currency: normalizeCurrencyInput(e.target.value) || 'TRY',
                }))}
                fullWidth
                sx={fieldSx}
              />
              <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 500, pt: 1 }}>
                Logo karşı gelir hesapları
              </Typography>
              <TextField
                label="Üye yemeği gelir hesabı"
                placeholder={memberDefault}
                value={config?.memberMealLogoIncomeArpCode ?? memberDefault}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  memberMealLogoIncomeArpCode: e.target.value,
                }))}
                helperText={logoIncomeHelper(memberDefault, config?.memberMealLogoIncomeArpCodeOverridden)}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Misafir / eş yemeği gelir hesabı"
                placeholder={guestDefault}
                value={config?.guestMealLogoIncomeArpCode ?? guestDefault}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  guestMealLogoIncomeArpCode: e.target.value,
                }))}
                helperText={logoIncomeHelper(guestDefault, config?.guestMealLogoIncomeArpCodeOverridden)}
                fullWidth
                sx={fieldSx}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeeklyMeetingConfigDialog;
